import React, { useState } from "react";
import {
  Button,
  Dialog,
  Card,
  CardBody,
  CardFooter,
  Typography,
  Input,
} from "@material-tailwind/react";
import { XCircleIcon } from "@heroicons/react/24/outline";
import { useHttp } from "@/hooks/useFetch";
import { useGlobalData } from "../Layout/GlobalContext";
import { ConfrimModal } from "../Layout/Confrim";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const isValidEmail = (v: string) => EMAIL_RE.test(v.trim());

interface ForgotPasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * 忘记密码 Dialog
 *
 * 流程：
 * 1. 输入邮箱 → 点"发送验证码"
 *    - 后端 IP 限流 + 邮箱 60s 限频 + 邮箱必须已注册
 *    - mock 模式：弹窗展示 6 位验证码便于联调
 * 2. 输入验证码 + 新密码 + 确认密码
 * 3. 点"重置密码"
 *    - 后端校验邮箱验证码 + 更新密码（md5）
 *    - 出于安全考虑，后端不颁发 token，前端关闭弹窗并提示用户回到登录页
 *
 * UI 与 Register.tsx 保持高度一致，降低用户学习成本
 */
export const ForgotPasswordDialog: React.FC<ForgotPasswordDialogProps> = ({
  open,
  onClose,
}) => {
  const { Http } = useHttp();
  const { alert, resetPasswordByEmail, setLoginModal } = useGlobalData();

  const [email, setEmail] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [password, setPassword] = useState("");
  const [password2, setPassword2] = useState("");
  const [submit, setSubmit] = useState(false);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);

  // mock 模式：后端把验证码塞进 Response.mockCode 便于本地联调
  const [mockCode, setMockCode] = useState("");
  const [mockOpen, setMockOpen] = useState(false);

  const [countdown, setCountdown] = useState(0);
  const countdownRef = React.useRef(0);

  const resetForm = () => {
    setEmail("");
    setEmailCode("");
    setPassword("");
    setPassword2("");
    setSubmit(false);
  };

  const handleClose = () => {
    onClose();
  };

  const startCountdown = () => {
    setCountdown(60);
    countdownRef.current = 60;
    const timer = window.setInterval(() => {
      countdownRef.current -= 1;
      setCountdown(countdownRef.current);
      if (countdownRef.current <= 0) {
        window.clearInterval(timer);
      }
    }, 1000);
  };

  const handleSendCode = async () => {
    setSubmit(true);
    if (!isValidEmail(email)) {
      alert({ color: "red", message: "请输入有效的邮箱地址" });
      return;
    }
    setSending(true);
    try {
      const data: any = await Http.post("/app/user/login/emailCode-reset", {
        email: email.trim(),
      });
      if (data?.mockCode) {
        setMockCode(String(data.mockCode));
        setMockOpen(true);
      } else {
        alert({ color: "green", message: "验证码已发送到邮箱，请注意查收" });
      }
      startCountdown();
    } catch (error) {
      // Http 拦截器已统一弹错（邮箱未注册 / 60s 限频 / IP 超限），此处兜底
    } finally {
      setSending(false);
    }
  };

  const handleReset = async () => {
    setSubmit(true);
    if (!isValidEmail(email)) {
      alert({ color: "red", message: "请输入有效的邮箱地址" });
      return;
    }
    if (!emailCode) {
      alert({ color: "red", message: "请输入邮箱验证码" });
      return;
    }
    if (!password || password.length < 6) {
      alert({ color: "red", message: "新密码至少 6 位" });
      return;
    }
    if (password !== password2) {
      alert({ color: "red", message: "两次密码不一致" });
      return;
    }
    setLoading(true);
    try {
      await resetPasswordByEmail(email.trim(), password, emailCode.trim());
      alert({ color: "green", message: "密码重置成功，请使用新密码登录" });
      resetForm();
      handleClose();
      // 引导用户用新密码重新登录
      setLoginModal(true);
    } catch (error) {
      // resetPasswordByEmail 内部已 alert，这里兜底
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        size="md"
        open={open}
        handler={onClose}
        className="bg-transparent shadow-none"
      >
        <Card className="mx-auto w-full md:max-w-[24rem]">
          <CardBody className="flex flex-col gap-4">
            <XCircleIcon
              className="absolute w-7 right-5 top-5 cursor-pointer"
              onClick={handleClose}
            />
            <Typography variant="h4" color="blue-gray">
              忘记密码
            </Typography>
            <Typography
              className="-mt-2 text-xs"
              variant="small"
              color="gray"
            >
              通过邮箱验证码重置密码
            </Typography>

            <Typography className="-mb-2" variant="h6">
              邮箱
            </Typography>
            <Input
              error={submit && !isValidEmail(email)}
              type="email"
              value={email}
              onChange={({ target }) => setEmail(target.value)}
              crossOrigin=""
              label="注册时使用的邮箱"
              size="lg"
              autoComplete="email"
            />

            <Typography className="-mb-2" variant="h6">
              邮箱验证码
            </Typography>
            <div className="relative flex w-full">
              <Input
                error={submit && !emailCode}
                type="text"
                maxLength={6}
                value={emailCode}
                onChange={({ target }) => setEmailCode(target.value)}
                crossOrigin=""
                label="6 位验证码"
                size="lg"
                className="pr-32"
                containerProps={{ className: "min-w-0" }}
              />
              <Button
                onClick={handleSendCode}
                size="sm"
                disabled={!!countdown || sending}
                className="!absolute right-1.5 top-1.5 rounded"
              >
                {countdown
                  ? `${countdown}s`
                  : sending
                  ? "发送中..."
                  : "发送验证码"}
              </Button>
            </div>

            <Typography className="-mb-2" variant="h6">
              新密码
            </Typography>
            <Input
              error={submit && (!password || password.length < 6)}
              type="password"
              value={password}
              onChange={({ target }) => setPassword(target.value)}
              crossOrigin=""
              label="至少 6 位"
              size="lg"
              autoComplete="new-password"
            />
            <Input
              error={submit && password !== password2}
              type="password"
              value={password2}
              onChange={({ target }) => setPassword2(target.value)}
              crossOrigin=""
              label="确认新密码"
              size="lg"
              autoComplete="new-password"
            />
          </CardBody>
          <CardFooter className="pt-0">
            <Button
              variant="gradient"
              onClick={handleReset}
              fullWidth
              loading={loading}
              disabled={loading}
            >
              重置密码
            </Button>
            <Typography variant="small" className="mt-3 flex justify-center">
              想起来了？
              <Typography
                as="a"
                href="#login"
                variant="small"
                color="blue-gray"
                className="ml-1 font-bold cursor-pointer"
                onClick={(e) => {
                  e.preventDefault();
                  handleClose();
                  setLoginModal(true);
                }}
              >
                返回登录
              </Typography>
            </Typography>
          </CardFooter>
        </Card>
      </Dialog>

      <ConfrimModal
        header={
          <div className="text-xl font-bold text-center w-full mt-5">
            邮箱验证码（开发模式）
          </div>
        }
        content={
          <div className="text-3xl font-bold text-center w-full tracking-widest">
            {mockCode}
          </div>
        }
        open={mockOpen}
        onConfirm={() => setMockOpen(false)}
        onCancel={() => setMockOpen(false)}
      />
    </>
  );
};
